import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';

import { getComments, postLike } from 'pages/Home/helper';
import { ENDPOINT } from 'constants/endpoints';
import { ROUTE } from 'constants/route';
import { NetworkActions } from 'helpers/actions/network';
import { JobActions } from 'helpers/actions/job';
import { Role } from 'helpers/user';
import { FirmActions } from 'helpers/actions/firm';

export const action = {
  feed: {
    post: {
      remove: (api, id, { grid }) =>
        ConfirmDialog({
          dismissWhenClose: false,
          bodyRender: 'Are you sure you want to delete this post?',
        }).then(() => {
          api.post(
            ENDPOINT.ACTION.FEED.DELETE_MOOD,
            { id },
            {
              handleError: response => ResponseErrorDialog({ response }),
              handleSuccess: () => grid.actions.fetch(),
            }
          );
        }),
    },

    like: (api, user, { NewsID: newsId }, { grid }) => {
      if (Role.isCandidate(user, true)) {
        api.post(
          ENDPOINT.ACTION.FEED.POST_LIKE,
          { newsId },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: responseData => {
              grid.actions.setDataRows(postLike(responseData, grid.data.rows));
            },
          }
        );
      }
    },

    comments: {
      showPrevious: (api, { NewsID: newsId, Comments }, { grid }) => {
        const limit = 10 + (Comments ? Comments.length : 0);
        api.get(
          ENDPOINT.ACTION.FEED.GET_COMMENTS,
          { newsId, limit },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: responseData => {
              grid.actions.setDataRows(
                getComments(responseData, grid.data.rows)
              );
            },
          }
        );
      },

      add: ({ newsId, count, comments }, grid) => {
        const updatedRows = cloneDeep(grid.data.rows);
        updatedRows.forEach(row => {
          if (row.NewsID === newsId) {
            const { Comments } = row;
            row.CommentCount = count;
            row.Comments = Comments ? [...Comments, ...comments] : comments;
          }
        });
        grid.actions.setDataRows(updatedRows);
      },

      remove: ({ NewsID, CommentID }, grid) => {
        grid.actions.updateDataRows(rows => {
          return rows.map(row => {
            if (row.NewsID !== NewsID) {
              return row;
            }

            const { Comments } = row;
            row.CommentCount = row.CommentCount - 1;
            row.Comments = Comments
              ? Comments.filter(
                  comment => comment.CommentID !== CommentID && !comment.deleted
                )
              : null;

            return row;
          });
        });
      },
    },
  },

  job: {
    apply: (api, history, user, { job }, { grid }) => {
      const action = new JobActions(api, history, user);

      return action.apply(job).then(success => success && grid.actions.fetch());
    },

    checkApply: (api, history, user, { job }, { updateUserResume }) => {
      const action = new JobActions(api, history, user);
      return action.checkingApply(job, updateUserResume);
    },
  },

  sendMessage: (api, history, user, { post }) => {
    if (Role.isCandidate(user, true)) {
      const action = new NetworkActions();
      action.sendMessage(post.CandidateID, post.FullName);
    }
  },

  firm: {
    setRating: (api, user, { id, mark }, { grid }) => {
      const action = new FirmActions(api, user);
      action.setRating({ id, mark }).then(() => grid.actions.fetch());
    },

    beforeSetRating: (api, user) => {
      const action = new FirmActions(api, user);
      return action.checkRatingUpdate(user);
    },
  },

  popup: {
    show: (message, history) => {
      let text = '';

      if ('regCompleted' === message) {
        text = (
          <p
            style={{
              textAlign: 'left',
            }}
          >
            Thank you for authenticating your work email. You need to complete
            your full profile in order to have access to the full platform
            including the job board and compensation reviews.
          </p>
        );
        ConfirmDialog({
          popupStyles: {
            maxWidth: '642px',
            padding: '30px 25px 30px 25px',
          },
          bodyRender: text,
          textSuccessBtn: 'Create a profile',
          dismissWhenClose: true,
          textCancelBtn: 'No Thanks',
        }).then(() => history.push(ROUTE.CANDIDATE_ACCOUNT_REGISTRATION));
      } else {
        if ('candidateEmailConfirmedText' === message) {
          text = 'Your email address has been confirmed.';
        }
        if ('candidateActivated' === message) {
          text = 'Your GoBuyside application has been completed and approved.';
        }
        if ('candidateNotApproved' === message) {
          text =
            'You have completed your GoBuyside application. Our team will now review your information.';
        }
        if ('resumeRequestAccepted' === message) {
          text = 'Resume request accepted';
        }
        if ('resumeRequestDeclined' === message) {
          text = 'Resume request declined';
        }
        if ('processActionNotAvailable' === message) {
          text =
            'This role has been filled and is no longer receiving applications.';
        }
        BasicDialog({ bodyRender: text });
      }
    },
  },
};
